import { Fragment, useState } from 'react';
import { useSettings } from '../App/SettingsContext';

const QuoteSidebar = () => {
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [isQuoteSend, setIsQuoteSend] = useState(false);
  const {
    settings,
    loading,
    quoteProducts,
    sidebarOpen,
    closeSidebar,
    removeFromQuote,
    clearQuote,
  } = useSettings();

  const plugin_enabled = settings?.atqb_enable === 'true';
  const mobile_enabled = settings?.mob_enable === 'true';
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  if (!plugin_enabled) return null;

  if (isMobile && !mobile_enabled) return null;
  
  const isInitialLoading = loading && quoteProducts.length === 0;

  if (!settings) return null;

  /* ---------------- EMAIL VALIDATION ---------------- */
  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required.';
    }
    
    // Capital letters check
    if (/[A-Z]/.test(email)) {
      return 'Email must not contain capital letters.';
    }
    
    // Allow only lowercase letters, numbers, dot and @
    if (!/^[a-z0-9.@]+$/.test(email)) {
      return 'Email must not contain any special characters.';
    }
    
    // Email structure check
    if (!/^[a-z0-9.]+@[a-z0-9.]+\.[a-z]{2,}$/.test(email)) {
      return 'Email format is invalid. Example: name@example.com';
    }
    
    return null;
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    if (!userName.trim()) {
      alert('Please enter your name');
      return;
    }

    const emailError = validateEmail(userEmail);
    if (emailError) {
      alert(emailError);
      return;
    }

    setIsQuoteSend(true);
    
    try {
      await fetch(window.MCTQFW_GLOBALS.ajax_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          action: 'mctqfw_save_quote',
          user_name: userName,
          user_email: userEmail,
        }),
      });

      clearQuote();
      setUserName('');
      setUserEmail('');
      closeSidebar();
      alert('Quote sent successfully');
    } catch {
      alert('Something went wrong');
    } finally {
      setIsQuoteSend(false);
    }
  };

  return (
    <Fragment>
      {/* BACKDROP */}
      <div
        onClick={closeSidebar}
        style={{
          position: 'fixed',
          inset: 0,
          background: sidebarOpen ? 'rgba(0,0,0,0.55)' : 'transparent',
          opacity: sidebarOpen ? 1 : 0,
          pointerEvents: sidebarOpen ? 'auto' : 'none',
          transition: 'opacity 0.3s ease',
          zIndex: 9998,
        }}
      />

      {/* SIDEBAR */}
      <aside
        onClick={(e) => e.stopPropagation()}
        className="quote-sidebar-section"
        style={{ 
          width: settings.bar_width ? `${settings.bar_width}px` : '400px',
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.35s ease',
        }}
      >
        {/* HEADER */}
        <div className="header-section"
          style={{ 
            borderBottom: `1px ${settings.barhead_border_style} ${settings.barhead_border_color}`, 
            backgroundColor: settings.barhead_color,
            color: settings.barhead_title_color,
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 6px',
            zIndex: 9,
          }}
        >
          <div>
            <div style={{ fontSize: 24, fontWeight: 600 }}>
              My Quote List
            </div>
          </div>
          <button
            className="quote-close-btn"
            onClick={closeSidebar} 
          >
            <svg className="close-svg" width="35" viewBox="0 0 24 24">
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="currentColor"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>

        {/* PRODUCTS */}
        <div className="products-section" style={{ backgroundColor: settings.quote_list_backcolor }}>
          {isInitialLoading ? (
            <div className="overlay-loader">
              <div className="spinner"></div>
            </div>
          ) : quoteProducts.length === 0 ? (
            <div className="no-products-text">
              No products added yet
            </div>
          ) : (
            <>
              {isRemoving && (
                <div className="overlay-loader">
                  <div className="spinner"></div>
                </div>
              )}

              {quoteProducts.map((product) => (
                <div key={product.id} className="product-list-item">
                  <img
                    src={product.image}
                    alt={product.title}
                    style={{
                      width: `${settings.pro_img_width}px`,
                      height: `${settings.pro_img_width}px`,
                      borderRadius: `${settings.pro_img_radius}px`,
                      objectFit: 'cover',
                    }}
                  />

                  <div style={{ flex: 1 }}>
                    <div className="quote-product-title" 
                      style={{ 
                        '--text-color': settings.pro_list_title_kcolor,
                        '--hover-color': settings.pro_title_hover_color,
                      }}
                    >
                      {product.title}
                    </div>
                    <div className="quote-product-price" style={{ color: settings.pro_price_color }}>
                      {product.currency}
                      {Number(product.price).toFixed(2)}
                    </div>
                  </div>

                  <button
                    className="quote-delete-btn"
                    label="Remove"
                    onClick={async () => {
                      setIsRemoving(true);
                      await removeFromQuote(product.id);
                      setIsRemoving(false);
                    }}
                  >
                    <svg className="delete-svg" width={25} height={25} viewBox="0 0 448 512" xmlns="http://www.w3.org/2000/svg" ><path d="M32 464C32 490.5 53.5 512 80 512h288c26.5 0 48-21.5 48-48V128H32V464zM304 208C304 199.1 311.1 192 320 192s16 7.125 16 16v224c0 8.875-7.125 16-16 16s-16-7.125-16-16V208zM208 208C208 199.1 215.1 192 224 192s16 7.125 16 16v224c0 8.875-7.125 16-16 16s-16-7.125-16-16V208zM112 208C112 199.1 119.1 192 128 192s16 7.125 16 16v224C144 440.9 136.9 448 128 448s-16-7.125-16-16V208zM432 32H320l-11.58-23.16c-2.709-5.42-8.25-8.844-14.31-8.844H153.9c-6.061 0-11.6 3.424-14.31 8.844L128 32H16c-8.836 0-16 7.162-16 16V80c0 8.836 7.164 16 16 16h416c8.838 0 16-7.164 16-16V48C448 39.16 440.8 32 432 32z" /></svg>
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        <div
          className="quote-form-input"
          style={{
            '--label-color': settings.form_title_color,
            backgroundColor: settings.footer_form_back_color,
            padding: 20,
            borderTop: '1px solid #d4cfcf',
            zIndex: 9,
            boxShadow: 'rgba(0, 0, 0, 0.2) 0px 0px 6px',
          }}
        >
          <label style={{ textTransform: 'none', fontWeight: 600, marginBottom: 10 }}>
            Your Name
          </label>
          <input
            type="text"
            placeHolder="Your Name..."
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
          />

          <label style={{ textTransform: 'none', fontWeight: 600, marginBottom:10, marginTop: 15 }}>
            Your Email
          </label>
          <input
            type="email"
            placeHolder="Your Email..."
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
          />
        </div>

        {/* CTA */}
        <div
          style={{
            borderTop: `1px solid ${settings.footer_border_color}`,
            backgroundColor: settings.footer_back_color,
            zIndex: 99,
            padding: 20,
          }}
        >
          <button
            isPrimary
            className="mctqfw_save_button"
            onClick={handleSubmit}
            isBusy={isQuoteSend}
            disabled={isQuoteSend}
            style={{
              width: '100%',
              '--button-bg-color': settings.footer_btn_back_color,
              '--button-font-color': settings.footer_btn_text_color,
              '--button-hover-bg-color': settings.footer_btn_back_hover_color,
              '--button-hover-text-color': settings.footer_btn_text_hover_color,
            }}
          >
            {settings.footer_btn_text}
          </button>
        </div>
      </aside>
    </Fragment>
  );
};

export default QuoteSidebar;
